import React, { useEffect, useState } from 'react';

import { ReactComponent as LikeIcon } from '@assets/icons/costing/like.svg';
import { ReactComponent as DislikeIcon } from '@assets/icons/costing/unlike.svg';
import { ReactComponent as LikedIcon } from '@assets/icons/costing/liked.svg';
import { ReactComponent as UnlikedIcon } from '@assets/icons/costing/unliked.svg';

import LoaderComponent from '@components/LoaderComponent';
import { toastSuccess } from '@components/Toast';

import { useSaveQtyWisePriceMutation } from '@features/costing-agent/api/costingAgentApi';

import CommonImage from '@/components/CommonImage';

import { getQuantityWisePrice } from './../utils';

// sectionKey falls back to key when the summary row and the costing table share a name
const SUMMARY_ROWS = [
	{ key: 'fabricConsumption', label: 'Fabric Consumption', sectionKey: 'fabricCost', unit: 'kg' },
	{ key: 'fabricCost', label: 'Fabric Cost' },
	{ key: 'printCost', label: 'Print Cost' },
	{ key: 'embroideryCost', label: 'Embroidery Cost' },
	{ key: 'trimsCost', label: 'Trims Cost' },
	{ key: 'cmCost', label: 'CM Cost' },
	{ key: 'washCost', label: 'Wash Cost' },
	{ key: 'packaging', label: 'Packaging' },
	{ key: 'commercial', label: 'Commercial' },
	{ key: 'perPisTestingCost', label: 'Testing' }
];

const CostingSummary = ({
	summary = {},
	totalFobPerUnit,
	isLiked,
	onLike,
	onDislike,
	isLoading = false,
	baseQty,
	historyId,
	quantityWisePrices,
	onSectionClick
}) => {
	const [saveQtyWisePrice, { isLoading: qtyWiseLoading }] = useSaveQtyWisePriceMutation();
	const [breakdowns, setBreakdowns] = useState([]);

	useEffect(() => {
		let tmpBreakdowns = [];
		if (quantityWisePrices && quantityWisePrices.length > 0) {
			tmpBreakdowns = quantityWisePrices.map((item) => ({
				id: item.id,
				quantity: item.qty,
				price: item.price
			}));
		}
		tmpBreakdowns.push({ quantity: '', price: '' });
		setBreakdowns(tmpBreakdowns);
	}, [quantityWisePrices]);

	const handleChange = (value, idx) => {
		const updatedBreakdowns = [...breakdowns];
		updatedBreakdowns[idx].quantity = value;
		updatedBreakdowns[idx].price = value ? getQuantityWisePrice(value, totalFobPerUnit, baseQty) : 0;
		if (idx === breakdowns.length - 1) {
			updatedBreakdowns.push({ quantity: '', price: '' });
		}
		setBreakdowns(updatedBreakdowns);
	};

	const handlePriceChange = (value, idx) => {
		const updatedBreakdowns = [...breakdowns];
		updatedBreakdowns[idx].price = value;
		setBreakdowns(updatedBreakdowns);
	};

	const removePrice = async (idx) => {
		await saveQtyWisePrice({
			historyId: historyId,
			requestBody: breakdowns
				.filter((item) => item.quantity && item.price)
				.filter((_, index) => index !== idx)
				.map((item) => {
					if (item.quantity && item.price) {
						return {
							qty: parseInt(item.quantity),
							price: parseFloat(item.price),
							id: item.id || undefined
						};
					}
				})
		}).then(() => {
			toastSuccess('Removed successfully');
			const updatedBreakdowns = breakdowns.filter((_, index) => index !== idx);
			setBreakdowns(updatedBreakdowns);
		});
	};

	const handleBlur = async (breakdown) => {
		if (!breakdown.quantity || !breakdown.price) {
			return;
		}
		await saveQtyWisePrice({
			historyId: historyId,
			requestBody: breakdowns
				.filter((item) => item.quantity && item.price)
				.map((item) => {
					if (item.quantity && item.price) {
						return {
							qty: parseInt(item.quantity),
							price: parseFloat(item.price),
							id: item.id || undefined
						};
					}
				})
		}).then(() => {
			toastSuccess('Quantity wise prices saved successfully');
		});
	};

	return (
		<LoaderComponent loading={qtyWiseLoading}>
			<div className="costing-summary border p-3">
				<div className="d-flex justify-content-between">
					<div>
						<p className="semibold-14 gray_dark_01">Costing Summary</p>
					</div>

					<div className="text-right">
						<p className="regular-12 gray_dark_01">FOB Per Unit</p>
						<div className="d-flex align-items-center align-items-center justify-content-end gap-1">
							<p className="price-title weight-500 gray_dark_01 pt-1">
								${(totalFobPerUnit || 0).toFixed(2)}
							</p>
						</div>
					</div>
				</div>
				<div className="summary-content mt-4">
					{SUMMARY_ROWS.map((row) => (
						<div
							key={row.key}
							className="summary-row d-flex justify-content-between pb-2 cursor-pointer"
							onClick={() => onSectionClick?.(row.sectionKey || row.key)}
						>
							<p className="regular-14 weight-500 gray_dark_02">{row.label}</p>
							<p className="regular-14 gray_dark_01">
								{row.unit
									? `${summary[row.key] || 0} ${row.unit}`
									: `$${Number(summary[row.key] || 0).toFixed(2)}`}
							</p>
						</div>
					))}
				</div>
				<div className="content-bottom border-top pt-3 d-flex justify-content-between">
					<p className="regular-12 gray_dark_02">Did you like this cost?</p>
					<div className="d-flex align-items-center gap-2">
						{isLiked === null || isLiked === undefined ? (
							<>
								<LikeIcon
									width={14}
									height={14}
									className="cursor-pointer"
									onClick={() => !isLoading && onLike && onLike()}
									style={{
										opacity: isLoading ? 0.5 : 1,
										pointerEvents: isLoading ? 'none' : 'auto'
									}}
								/>
								<DislikeIcon
									width={14}
									height={14}
									className="cursor-pointer"
									onClick={() => !isLoading && onDislike && onDislike()}
									style={{
										opacity: isLoading ? 0.5 : 1,
										pointerEvents: isLoading ? 'none' : 'auto'
									}}
								/>
							</>
						) : isLiked === true ? (
							<LikedIcon width={14} height={14} style={{ opacity: 1, cursor: 'default' }} />
						) : (
							<UnlikedIcon width={14} height={14} style={{ opacity: 1, cursor: 'default' }} />
						)}
					</div>
				</div>
			</div>
			<div className="costing-summary border p-3 mt-3">
				<div className="d-flex justify-content-between">
					<div>
						<p className="semibold-14 gray_dark_01">Quantity wise Costing</p>
					</div>
				</div>
				<div>
					<table className="quantity-wise-table table table-bordered height-sm mt-3">
						<thead>
							<tr>
								<th>Quantity</th>
								<th>FOB Per Unit</th>
							</tr>
						</thead>
						<tbody>
							<tr className="highlight-row">
								<td>{baseQty} (Base)</td>
								<td>${totalFobPerUnit}</td>
							</tr>
							{breakdowns.map((item, idx) => (
								<tr key={`breakdown_${idx}`}>
									<td>
										<input
											type="number"
											min={1}
											value={item.quantity}
											placeholder="Add quantity"
											onChange={(e) => handleChange(e.target.value, idx)}
											onBlur={() => handleBlur(item)}
										/>
									</td>
									<td>
										<div className={'d-flex align-items-center'}>
											<input
												type="number"
												value={item.price}
												placeholder="Add price"
												onChange={(e) => handlePriceChange(e.target.value, idx)}
												onBlur={() => handleBlur(item)}
											/>
											{breakdowns.length > 0 && (
												<span
													className="cursor-pointer"
													onClick={() => removePrice(idx)}
												>
													<CommonImage
														src="/icons/delete24.svg"
														alt="info"
														width={18}
														height={18}
														className="delete-icon cursor-pointer ml-2"
													/>
												</span>
											)}
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default CostingSummary;
